import { memo, useMemo } from 'react';
import { birthdayConfig } from '../config';

type Kind = 'flowers' | 'hearts' | 'sparkles';

type Props = {
  kind: Kind;
  count?: number;
  active?: boolean;
};

const sets: Record<Kind, string[]> = {
  flowers: ['🌸', '🌹', '🌺', '🌷', '🌼', '❀'],
  hearts: ['💗', '💖', '💕', '♥'],
  sparkles: ['✦', '✧', '⋆', '✨'],
};

export const ParticleRain = memo(function ParticleRain({ kind, count = 22, active = true }: Props) {
  const particles = useMemo(
    () =>
      Array.from({ length: count }, (_, index) => ({
        id: `${kind}-${index}`,
        symbol: sets[kind][index % sets[kind].length],
        left: (index * 37 + 9) % 100,
        delay: ((index * 19) % 90) / 10,
        duration: 8 + ((index * 13) % 70) / 10,
        size: 12 + ((index * 11) % 18),
        drift: -30 + ((index * 17) % 60),
        opacity: 0.26 + ((index * 7) % 55) / 100,
      })),
    [count, kind],
  );

  const enabled =
    active &&
    ((kind === 'flowers' && birthdayConfig.theme.enableFlowerRain) ||
      (kind === 'hearts' && birthdayConfig.theme.enableHeartRain) ||
      (kind === 'sparkles' && birthdayConfig.theme.enableSparkles));

  if (!enabled) return null;

  return (
    <div className={`particle-layer particle-${kind}`} aria-hidden="true">
      {particles.map((particle) => (
        <span
          key={particle.id}
          style={
            {
              '--left': `${particle.left}%`,
              '--delay': `${particle.delay}s`,
              '--duration': `${particle.duration}s`,
              '--size': `${particle.size}px`,
              '--drift': `${particle.drift}px`,
              '--opacity': particle.opacity,
            } as React.CSSProperties
          }
        >
          {particle.symbol}
        </span>
      ))}
    </div>
  );
});
